import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchParamsContext } from 'components/search/search.context';
import { LocationContext } from './quick-create-communication.types';

/**
 * Reads the Guest Journey report's current Locations filter (the `storeIds`
 * search param) so an entry point can carry it onto the drawer + the draft.
 *
 * Defensive on purpose: reads the context directly (not `useSafeContext`) and
 * returns `{}` when there is no search context or no location param — e.g. a tab
 * without the filter, or a unit test that renders the entry point in isolation —
 * so the callers stay additive.
 */
export const useQuickCreateLocationContext = (): LocationContext => {
  const { t } = useTranslation();
  const ctx = useContext(SearchParamsContext);
  const param = ctx?.searchParams?.storeIds;
  if (!param) return {};

  const value = ((param.value as string[]) ?? []).filter(Boolean);
  const specific = value.filter(id => id !== 'ALL');

  if (specific.length === 0) {
    return {
      storeIds: value,
      locationLabel: t('drawer.quickCreateCommunication.location.all'),
    };
  }

  const options = param.options ?? [];
  const labels = specific
    .map(id => options.find(option => option.id === id)?.label)
    .filter((label): label is string => !!label);

  return {
    storeIds: value,
    locationLabel:
      labels.length === specific.length
        ? labels.join(', ')
        : t('drawer.quickCreateCommunication.location.multiple', {
            count: specific.length,
          }),
  };
};
